const DB_NAME = 'focusTodoDB';
const DB_VERSION = 1;
const STORE_NAME = 'app';
const APP_STATE_KEY = 'appState';
const META_KEY = 'meta';

const MIGRATION_LOCK_KEY = 'focusTodoMigrationLock';
const LOCK_TTL_MS = 15000;
const LOCK_WAIT_MS = 8000;
const LOCK_POLL_MS = 250;

const SUPPORTED_THEMES = ['violet', 'rose', 'teal', 'black'];
const SUPPORTED_SIDEBAR_VIEWS = ['timer', 'notes'];

function nowIso() {
  return new Date().toISOString();
}

function safeJsonParse(value, fallback) {
  if (value === null || value === undefined) return fallback;
  try {
    return JSON.parse(value);
  } catch (err) {
    return fallback;
  }
}

function normalizeBoolean(value, fallback) {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

function normalizeNumber(value, fallback) {
  if (typeof value === 'number' && isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (!isNaN(parsed) && isFinite(parsed)) return parsed;
  }
  return fallback;
}

function normalizeTimestamp(value, fallback) {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length > 0 && !Number.isNaN(Date.parse(trimmed))) {
      return new Date(trimmed).toISOString();
    }
  }

  return fallback;
}

function normalizeTodoList(list, { isCompletedList = false } = {}) {
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const id = normalizeNumber(item.id, null);
      if (id === null) return null;
      const normalizedCreatedAt = normalizeTimestamp(item.createdAt, '');
      const normalizedCompletedAt = normalizeTimestamp(item.completedAt, '');
      const createdAt = normalizedCreatedAt;
      const completedAt = isCompletedList ? normalizedCompletedAt : '';

      return {
        id,
        text: (typeof item.text === 'string') ? item.text : '',
        done: !!item.done,
        createdAt,
        completedAt,
      };
    })
    .filter(Boolean);
}

function normalizeActivityLog(activityLog) {
  if (!Array.isArray(activityLog)) return [];

  return activityLog
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      return {
        time: (typeof entry.time === 'string') ? entry.time : '',
        text: (typeof entry.text === 'string') ? entry.text : '',
      };
    })
    .filter(Boolean);
}

function deriveHighestId(todos, completedTodos) {
  const merged = todos.concat(completedTodos);
  if (!merged.length) return 0;

  return merged.reduce((maxId, todo) => {
    return (todo.id > maxId) ? todo.id : maxId;
  }, 0);
}

function normalizeTheme(theme, fallback) {
  if (SUPPORTED_THEMES.indexOf(theme) >= 0) return theme;
  return fallback;
}

function normalizeSidebarView(view, fallback) {
  if (SUPPORTED_SIDEBAR_VIEWS.indexOf(view) >= 0) return view;
  return fallback;
}

function normalizeNotes(rawNotes) {
  const notes = (rawNotes && typeof rawNotes === 'object') ? rawNotes : {};
  return {
    markdown: (typeof notes.markdown === 'string') ? notes.markdown : '',
    updatedAt: (typeof notes.updatedAt === 'string') ? notes.updatedAt : '',
  };
}

function createDefaultState() {
  return {
    version: '3.0',
    name: '',
    highestId: 0,
    todos: [],
    completedTodos: [],
    activityLog: [],
    lastUsedDate: '',
    notes: {
      markdown: '',
      updatedAt: '',
    },
    settings: {
      darkMode: false,
      theme: 'violet',
      sidebarCollapsed: false,
      sidebarView: 'notes',
    },
  };
}

function normalizeState(rawState) {
  const defaults = createDefaultState();
  const raw = rawState || {};
  const rawSettings = raw.settings || {};

  const todos = normalizeTodoList(raw.todos, { isCompletedList: false });
  const completedTodos = normalizeTodoList(raw.completedTodos, { isCompletedList: true });
  const highestIdFromData = deriveHighestId(todos, completedTodos);

  const normalized = {
    version: (typeof raw.version === 'string') ? raw.version : defaults.version,
    name: (typeof raw.name === 'string') ? raw.name : defaults.name,
    highestId: normalizeNumber(raw.highestId, highestIdFromData),
    todos,
    completedTodos,
    activityLog: normalizeActivityLog(raw.activityLog),
    lastUsedDate: (typeof raw.lastUsedDate === 'string') ? raw.lastUsedDate : defaults.lastUsedDate,
    notes: normalizeNotes(raw.notes),
    settings: {
      darkMode: normalizeBoolean(rawSettings.darkMode, defaults.settings.darkMode),
      theme: normalizeTheme(rawSettings.theme, defaults.settings.theme),
      sidebarCollapsed: normalizeBoolean(rawSettings.sidebarCollapsed, defaults.settings.sidebarCollapsed),
      sidebarView: normalizeSidebarView(rawSettings.sidebarView, defaults.settings.sidebarView),
    },
  };

  if (normalized.highestId < highestIdFromData) {
    normalized.highestId = highestIdFromData;
  }

  return normalized;
}

class AppStorage {
  constructor() {
    this.db = null;
    this.backend = 'localStorage';
    this.initPromise = null;
    this.writeQueue = Promise.resolve();
  }

  initialize() {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.openIndexedDB()
      .then((db) => {
        this.db = db;
        this.backend = 'indexedDB';
        this.requestPersistentStorage();
        return this.runMigration();
      })
      .catch(() => {
        this.backend = 'localStorage';
        this.db = null;
        return Promise.resolve();
      });

    return this.initPromise;
  }

  requestPersistentStorage() {
    try {
      if (navigator && navigator.storage && navigator.storage.persist) {
        navigator.storage.persist().catch(() => {});
      }
    } catch (err) {
      // Ignore unsupported browser APIs.
    }
  }

  openIndexedDB() {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined' || !window.indexedDB) {
        reject(new Error('IndexedDB unavailable'));
        return;
      }

      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
      };

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onerror = () => {
        reject(request.error || new Error('Failed to open IndexedDB'));
      };
    });
  }

  isIndexedDBReady() {
    return !!this.db;
  }

  loadState() {
    return this.initialize().then(() => {
      if (this.isIndexedDBReady()) {
        return this.readRecord(APP_STATE_KEY)
          .then((storedState) => {
            if (storedState) {
              return {
                state: normalizeState(storedState),
                hasExistingData: true,
                source: 'indexedDB',
              };
            }

            const legacy = this.readLegacySnapshot();
            return {
              state: legacy.state,
              hasExistingData: legacy.hasExistingData,
              source: 'localStorageFallback',
            };
          })
          .catch(() => {
            const legacy = this.readLegacySnapshot();
            return {
              state: legacy.state,
              hasExistingData: legacy.hasExistingData,
              source: 'localStorageFallback',
            };
          });
      }

      const legacy = this.readLegacySnapshot();
      return {
        state: legacy.state,
        hasExistingData: legacy.hasExistingData,
        source: 'localStorage',
      };
    });
  }

  saveTodoState(todoState) {
    const partial = {
      version: '3.0',
      name: (typeof todoState.name === 'string') ? todoState.name : '',
      highestId: normalizeNumber(todoState.highestId, 0),
      todos: normalizeTodoList(todoState.todos, { isCompletedList: false }),
      completedTodos: normalizeTodoList(todoState.completedTodos, { isCompletedList: true }),
    };

    return this.persistState(partial);
  }

  saveActivityState(activityState) {
    const partial = {
      version: '3.0',
      activityLog: normalizeActivityLog(activityState.activityLog),
      lastUsedDate: (typeof activityState.lastUsedDate === 'string') ? activityState.lastUsedDate : '',
    };

    return this.persistState(partial);
  }

  saveSettings(settings) {
    const partialSettings = {};
    if (settings && Object.prototype.hasOwnProperty.call(settings, 'darkMode')) {
      partialSettings.darkMode = normalizeBoolean(settings.darkMode, false);
    }
    if (settings && Object.prototype.hasOwnProperty.call(settings, 'theme')) {
      partialSettings.theme = normalizeTheme(settings.theme, 'violet');
    }
    if (settings && Object.prototype.hasOwnProperty.call(settings, 'sidebarCollapsed')) {
      partialSettings.sidebarCollapsed = normalizeBoolean(settings.sidebarCollapsed, false);
    }
    if (settings && Object.prototype.hasOwnProperty.call(settings, 'sidebarView')) {
      partialSettings.sidebarView = normalizeSidebarView(settings.sidebarView, 'notes');
    }

    return this.persistState({
      version: '3.0',
      settings: partialSettings,
    });
  }

  saveNotes(notesState) {
    return this.persistState({
      version: '3.0',
      notes: normalizeNotes(notesState),
    });
  }

  persistState(partialState) {
    return this.enqueueWrite(() => {
      return this.loadState().then((result) => {
        const currentState = result.state;
        const nextState = normalizeState(Object.assign({}, currentState, partialState, {
          settings: Object.assign({}, currentState.settings, partialState.settings || {}),
        }));

        if (this.isIndexedDBReady()) {
          return this.writeRecord(APP_STATE_KEY, nextState)
            .then(() => {
              this.writeLegacySnapshot(nextState);
              return nextState;
            })
            .catch(() => {
              this.writeLegacySnapshot(nextState);
              return nextState;
            });
        }

        this.writeLegacySnapshot(nextState);
        return nextState;
      });
    });
  }

  enqueueWrite(operation) {
    this.writeQueue = this.writeQueue.then(operation, operation);
    return this.writeQueue;
  }

  runMigration() {
    if (!this.isIndexedDBReady()) {
      return Promise.resolve();
    }

    return this.withMigrationLock(() => {
      return this.migrateFromLegacyLocalStorage();
    });
  }

  withMigrationLock(operation) {
    const owner = this.tryAcquireMigrationLock();
    if (owner) {
      return Promise.resolve()
        .then(operation)
        .then((result) => {
          this.releaseMigrationLock(owner);
          return result;
        })
        .catch((err) => {
          this.releaseMigrationLock(owner);
          throw err;
        });
    }

    return this.waitForMigrationCompletion().then((completed) => {
      if (completed) {
        return Promise.resolve();
      }

      const retryOwner = this.tryAcquireMigrationLock();
      if (!retryOwner) {
        return Promise.resolve();
      }

      return Promise.resolve()
        .then(operation)
        .then((result) => {
          this.releaseMigrationLock(retryOwner);
          return result;
        })
        .catch((err) => {
          this.releaseMigrationLock(retryOwner);
          throw err;
        });
    });
  }

  tryAcquireMigrationLock() {
    try {
      const now = Date.now();
      const owner = `${now}-${Math.random().toString(16).slice(2)}`;
      const existingRaw = localStorage.getItem(MIGRATION_LOCK_KEY);
      const existingLock = safeJsonParse(existingRaw, null);

      if (existingLock && existingLock.ts && (now - existingLock.ts < LOCK_TTL_MS)) {
        return null;
      }

      localStorage.setItem(MIGRATION_LOCK_KEY, JSON.stringify({ owner, ts: now }));
      const confirmed = safeJsonParse(localStorage.getItem(MIGRATION_LOCK_KEY), null);
      if (confirmed && confirmed.owner === owner) {
        return owner;
      }
      return null;
    } catch (err) {
      // If localStorage lock cannot be used, proceed without lock.
      return `fallback-${Date.now()}`;
    }
  }

  releaseMigrationLock(owner) {
    try {
      const lock = safeJsonParse(localStorage.getItem(MIGRATION_LOCK_KEY), null);
      if (!lock || lock.owner === owner || (typeof owner === 'string' && owner.indexOf('fallback-') === 0)) {
        localStorage.removeItem(MIGRATION_LOCK_KEY);
      }
    } catch (err) {
      // ignore release errors
    }
  }

  waitForMigrationCompletion() {
    if (!this.isIndexedDBReady()) {
      return Promise.resolve(true);
    }

    const start = Date.now();

    return new Promise((resolve) => {
      const check = () => {
        this.readRecord(META_KEY)
          .then((meta) => {
            const complete = meta && meta.schemaVersion === DB_VERSION && meta.migrationState === 'complete';
            if (complete) {
              resolve(true);
              return;
            }

            if (Date.now() - start >= LOCK_WAIT_MS) {
              resolve(false);
              return;
            }

            setTimeout(check, LOCK_POLL_MS);
          })
          .catch(() => {
            if (Date.now() - start >= LOCK_WAIT_MS) {
              resolve(false);
              return;
            }
            setTimeout(check, LOCK_POLL_MS);
          });
      };

      check();
    });
  }

  migrateFromLegacyLocalStorage() {
    return Promise.all([
      this.readRecord(APP_STATE_KEY),
      this.readRecord(META_KEY),
    ]).then(([appState, meta]) => {
      const migrationComplete = meta && meta.schemaVersion === DB_VERSION && meta.migrationState === 'complete';
      if (migrationComplete && appState) {
        return Promise.resolve();
      }

      if (appState) {
        return this.writeRecord(META_KEY, {
          schemaVersion: DB_VERSION,
          migrationState: 'complete',
          migratedAt: nowIso(),
          sourceHash: this.createStateHash(appState),
        });
      }

      const legacy = this.readLegacySnapshot();
      const migratedState = normalizeState(legacy.state);

      const pendingMeta = {
        schemaVersion: DB_VERSION,
        migrationState: 'pending',
        startedAt: nowIso(),
      };

      return this.writeRecord(META_KEY, pendingMeta)
        .then(() => {
          if (!legacy.hasExistingData) {
            const completeMeta = {
              schemaVersion: DB_VERSION,
              migrationState: 'complete',
              migratedAt: nowIso(),
              sourceHash: null,
            };
            return this.writeRecord(META_KEY, completeMeta);
          }

          const sourceHash = this.createStateHash(migratedState);
          const completeMeta = {
            schemaVersion: DB_VERSION,
            migrationState: 'complete',
            migratedAt: nowIso(),
            sourceHash,
          };

          return this.writeRecords([
            { key: APP_STATE_KEY, value: migratedState },
            { key: META_KEY, value: completeMeta },
          ])
            .then(() => this.readRecord(APP_STATE_KEY))
            .then((readBackState) => {
              const migratedHash = this.createStateHash(migratedState);
              const readBackHash = this.createStateHash(normalizeState(readBackState));

              if (migratedHash !== readBackHash) {
                throw new Error('IndexedDB migration verification failed');
              }
            });
        });
    });
  }

  createStateHash(state) {
    const normalized = normalizeState(state);
    const payload = JSON.stringify({
      version: normalized.version,
      name: normalized.name,
      highestId: normalized.highestId,
      todos: normalized.todos,
      completedTodos: normalized.completedTodos,
      activityLog: normalized.activityLog,
      lastUsedDate: normalized.lastUsedDate,
      notes: normalized.notes,
      settings: normalized.settings,
    });

    let hash = 0;
    for (let i = 0; i < payload.length; i += 1) {
      hash = ((hash << 5) - hash) + payload.charCodeAt(i);
      hash |= 0;
    }

    return `h${Math.abs(hash)}`;
  }

  readLegacySnapshot() {
    const defaults = createDefaultState();

    let name = '';
    let todos = [];
    let completedTodos = [];
    let activityLog = [];
    let highestId = null;
    let version = null;
    let lastUsedDate = '';
    let notesMarkdown = '';
    let notesUpdatedAt = '';
    let theme = defaults.settings.theme;
    let darkMode = defaults.settings.darkMode;
    let sidebarCollapsed = defaults.settings.sidebarCollapsed;
    let sidebarView = defaults.settings.sidebarView;
    let hasThemePreference = false;
    let hasDarkModePreference = false;
    let hasSidebarPreference = false;
    let hasSidebarViewPreference = false;
    let hasNotesPreference = false;

    try {
      version = safeJsonParse(localStorage.getItem('version'), null);
      name = safeJsonParse(localStorage.getItem('name'), '');
      todos = safeJsonParse(localStorage.getItem('todoList'), []);
      completedTodos = safeJsonParse(localStorage.getItem('completedTodoList'), []);
      highestId = safeJsonParse(localStorage.getItem('highestId'), null);
      activityLog = safeJsonParse(localStorage.getItem('activityLog'), []);
      lastUsedDate = localStorage.getItem('lastUsedDate') || '';
      notesMarkdown = localStorage.getItem('notesMarkdown') || '';
      notesUpdatedAt = localStorage.getItem('notesUpdatedAt') || '';

      const storedTheme = localStorage.getItem('theme');
      theme = normalizeTheme(storedTheme, defaults.settings.theme);
      darkMode = normalizeBoolean(localStorage.getItem('darkMode'), defaults.settings.darkMode);
      sidebarCollapsed = normalizeBoolean(localStorage.getItem('sidebarCollapsed'), defaults.settings.sidebarCollapsed);
      sidebarView = normalizeSidebarView(localStorage.getItem('sidebarView'), defaults.settings.sidebarView);
      hasThemePreference = storedTheme !== null && storedTheme !== undefined;
      hasDarkModePreference = localStorage.getItem('darkMode') !== null;
      hasSidebarPreference = localStorage.getItem('sidebarCollapsed') !== null;
      hasSidebarViewPreference = localStorage.getItem('sidebarView') !== null;
      hasNotesPreference = localStorage.getItem('notesMarkdown') !== null;
    } catch (err) {
      // Keep defaults when localStorage is unavailable.
    }

    const normalizedState = normalizeState({
      version: (version !== null && version !== undefined) ? String(version) : defaults.version,
      name: (typeof name === 'string') ? name : defaults.name,
      highestId,
      todos,
      completedTodos,
      activityLog,
      lastUsedDate,
      notes: {
        markdown: notesMarkdown,
        updatedAt: notesUpdatedAt,
      },
      settings: {
        darkMode,
        theme,
        sidebarCollapsed,
        sidebarView,
      },
    });

    const hasExistingData = (
      !!name
      || (Array.isArray(todos) && todos.length > 0)
      || (Array.isArray(completedTodos) && completedTodos.length > 0)
      || (Array.isArray(activityLog) && activityLog.length > 0)
      || (highestId !== null && highestId !== undefined)
      || !!lastUsedDate
      || (version !== null && version !== undefined)
      || hasThemePreference
      || hasDarkModePreference
      || hasSidebarPreference
      || hasSidebarViewPreference
      || hasNotesPreference
    );

    return {
      state: normalizedState,
      hasExistingData,
    };
  }

  writeLegacySnapshot(state) {
    const normalized = normalizeState(state);

    try {
      localStorage.setItem('version', normalized.version);
      localStorage.setItem('name', JSON.stringify(normalized.name));
      localStorage.setItem('highestId', JSON.stringify(normalized.highestId));
      localStorage.setItem('todoList', JSON.stringify(normalized.todos));
      localStorage.setItem('completedTodoList', JSON.stringify(normalized.completedTodos));
      localStorage.setItem('activityLog', JSON.stringify(normalized.activityLog));
      localStorage.setItem('lastUsedDate', normalized.lastUsedDate || '');
      localStorage.setItem('theme', normalized.settings.theme);
      localStorage.setItem('darkMode', String(!!normalized.settings.darkMode));
      localStorage.setItem('sidebarCollapsed', String(!!normalized.settings.sidebarCollapsed));
      localStorage.setItem('sidebarView', normalized.settings.sidebarView);
      localStorage.setItem('notesMarkdown', normalized.notes.markdown || '');
      localStorage.setItem('notesUpdatedAt', normalized.notes.updatedAt || '');
    } catch (err) {
      // Ignore localStorage write failures.
    }
  }

  readRecord(key) {
    return new Promise((resolve, reject) => {
      if (!this.isIndexedDBReady()) {
        resolve(null);
        return;
      }

      const tx = this.db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        const record = request.result;
        resolve(record ? record.value : null);
      };

      request.onerror = () => {
        reject(request.error || new Error('IndexedDB read failed'));
      };
    });
  }

  writeRecord(key, value) {
    return this.writeRecords([{ key, value }]);
  }

  writeRecords(records) {
    return new Promise((resolve, reject) => {
      if (!this.isIndexedDBReady()) {
        resolve();
        return;
      }

      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      records.forEach((record) => {
        store.put(record);
      });

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error('IndexedDB write failed'));
      tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted'));
    });
  }
}

const storage = new AppStorage();

export default storage;
